import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ActivityIndicator,
} from "react-native";
import { SearchBar } from "../components/shared/component/SearchBar";
import HeaderBack from "../components/shared/component/HeaderBack";
import { menuStyles } from "../components/shared/styles/menuStyles";
import { LocationPermissionHandler } from "../components/shared/component/LocationPermissionHandler";
import { registrationStyles } from "../components/shared/styles/registrationStyles";
import { FlickOColors } from "../components/shared/styles/color";
import LocationDetails from "../components/shared/component/LocationDetails";
import { Navigation, MapPin, Target } from "lucide-react-native";
import { getResponsiveSize } from "../components/shared/styles/globalStyles";
import { NavigationProps } from "../types/navigation";
import { useNavigation } from "@react-navigation/native";
import { Coordinates } from "../types/user";
import { reverseGeocodingService } from "../components/shared/services/reverseGeocoding.service";
import { CompactLoadingSpinner } from "../components/shared/LoadingSpinner";

const DeliveryLocationScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProps>();
  const [coordinates, setCoordinates] = useState<Coordinates>();
  const [searchQuery, setSearchQuery] = useState("");
  const [address, setAddress] = useState("");
  const [showLoader, setShowLoader] = useState(address === "");

  const handleLocationUpdate = async (newCoordinates: Coordinates) => {
    console.log("handleLocationUpdate called");
    setCoordinates(newCoordinates);
    const add: string = await reverseGeocodingService.getAddressAPI(
      newCoordinates
    );
    setAddress(add);
  };

  useEffect(() => {
    if (address === "") {
      setShowLoader(true);
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowLoader(false);
    }
  }, [address]);

  const handleCurrentLocation = async () => {
    console.log("handleCurrentLocation called");
    const result = await LocationPermissionHandler.getCurrentLocation();
    console.log("result:", result);
    if (result.status === "granted" && result.coordinates) {
      console.log("result.coordinates:", result.coordinates);
      setCoordinates(result.coordinates);
      const add: string = await reverseGeocodingService.getAddressAPI(
        result.coordinates
      );
      setAddress(add);
    }
  };

  const handleConfirmLocation = () => {
    console.log("coordinates: ", coordinates, ", handleConfirmLocation called");
    if (coordinates) {
      navigation.navigate("AddressDetailsScreen", {
        locationDetails: {
          coordinates,
          fullAddress: address,
          locality: "Bander Pakhadi",
          city: "Mumbai",
        },
      });
    }
  };

  return (
    <SafeAreaView
      style={[
        registrationStyles.container,
        styles.container,
        { backgroundColor: FlickOColors.white },
      ]}
    >
      <HeaderBack title="Select Delivery Location" />

      {/* Searchbar with hint text */}
      <View style={[styles.searchSection]}>
        <SearchBar
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search for your society, apartment or area"
          iconColor={FlickOColors.text.secondary}
        />
        <Text style={styles.searchHint}>
          Tap anywhere on the map to set delivery location
        </Text>
      </View>

      {/* Map section with location indicator */}
      <View style={{ flex: 1 }}>
        <LocationDetails
          coordinates={coordinates}
          onUpdate={handleLocationUpdate}
          showMap={true}
        />
        <View style={styles.accuracyIndicator}>
          <Target size={getResponsiveSize(16)} color={FlickOColors.success} />
          <Text style={styles.accuracyText}>GPS Accuracy: High</Text>
        </View>
      </View>

      {/* Bottom section with enhanced visual hierarchy */}
      <View style={styles.bottomSection}>
        <TouchableOpacity
          style={styles.currentLocationButton}
          onPress={handleCurrentLocation}
        >
          <Navigation
            size={getResponsiveSize(24)}
            color={FlickOColors.primary}
          />
          <Text style={styles.currentLocationText}>Use Current Location</Text>
          <View style={styles.accuracyBadge}>
            <Text style={styles.accuracyBadgeText}>Fastest</Text>
          </View>
        </TouchableOpacity>

        <View style={styles.locationInfo}>
          <View style={styles.addressContainer}>
            <View style={styles.addressIconContainer}>
              <MapPin
                size={getResponsiveSize(24)}
                color={FlickOColors.primary}
              />
            </View>
            <View style={styles.addressTextContainer}>
              {showLoader ? (
                <CompactLoadingSpinner />
              ) : (
                <>
                  <Text style={styles.locationTitle}>
                    {address && address.includes(",")
                      ? address.split(",")[0]
                      : address || "No location name"}
                  </Text>
                  <Text style={styles.locationAddress} numberOfLines={2}>
                    {address && address.includes(",")
                      ? address.split(",").slice(1).join(",").trimStart()
                      : "No address available"}
                  </Text>
                </>
              )}
            </View>
          </View>
        </View>

        <TouchableOpacity
          style={[registrationStyles.submitButton, styles.confirmButton]}
          onPress={handleConfirmLocation}
        >
          <Text style={registrationStyles.submitButtonText}>
            CONFIRM LOCATION
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  searchSection: {
    padding: getResponsiveSize(16),
    backgroundColor: FlickOColors.white,
  },
  searchHint: {
    marginTop: getResponsiveSize(8),
    fontSize: getResponsiveSize(12),
    color: FlickOColors.text.secondary,
    textAlign: "center",
  },
  mapSection: {
    flex: 1,
    position: "relative",
  },
  accuracyIndicator: {
    position: "absolute",
    top: getResponsiveSize(16),
    right: getResponsiveSize(16),
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: FlickOColors.white,
    padding: getResponsiveSize(8),
    borderRadius: getResponsiveSize(20),
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  accuracyText: {
    marginLeft: getResponsiveSize(4),
    fontSize: getResponsiveSize(12),
    color: FlickOColors.success,
    fontWeight: "500",
  },
  bottomSection: {
    backgroundColor: FlickOColors.white,
    padding: getResponsiveSize(16),
    borderTopWidth: 1,
    borderTopColor: FlickOColors.gray.light,
  },
  currentLocationButton: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: getResponsiveSize(16),
    backgroundColor: `${FlickOColors.primary}08`,
    padding: getResponsiveSize(12),
    borderRadius: getResponsiveSize(8),
  },
  currentLocationText: {
    marginLeft: getResponsiveSize(12),
    color: FlickOColors.primary,
    fontSize: getResponsiveSize(16),
    fontWeight: "500",
    flex: 1,
  },
  accuracyBadge: {
    backgroundColor: FlickOColors.success,
    paddingHorizontal: getResponsiveSize(8),
    paddingVertical: getResponsiveSize(4),
    borderRadius: getResponsiveSize(12),
  },
  accuracyBadgeText: {
    color: FlickOColors.white,
    fontSize: getResponsiveSize(12),
    fontWeight: "500",
  },
  locationInfo: {
    marginBottom: getResponsiveSize(16),
  },
  addressContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: FlickOColors.background.light,
    padding: getResponsiveSize(12),
    borderRadius: getResponsiveSize(8),
  },
  addressIconContainer: {
    backgroundColor: FlickOColors.white,
    padding: getResponsiveSize(8),
    borderRadius: getResponsiveSize(8),
  },
  addressTextContainer: {
    flex: 1,
    marginHorizontal: getResponsiveSize(12),
  },
  locationTitle: {
    fontSize: getResponsiveSize(18),
    fontWeight: "500",
    marginBottom: getResponsiveSize(4),
  },
  locationAddress: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.secondary,
  },
  changeButton: {
    marginLeft: getResponsiveSize(8),
  },
  confirmButton: {
    marginBottom: 0,
  },
});

export default DeliveryLocationScreen;
